import { Result } from 'antd';
import Loader from 'Common/Loader';
import DataGrid, { Pager, Paging, Column } from 'devextreme-react/cjs/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import { getAllEnquiryData } from '../services';

interface EnquiryData {
  id: string;
  customerName: string;
  emailId: string;
  purpose: string;
  mobileNo: string;
  status: string;
  type: string;
}

interface PaginationState {
  pageSize: number;
  pageNumber: number;
}

const EnquiryMasterMain: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const dataGridRef = useRef<DataGrid>(null);
  const [permissionDetails, setPermissionDetails] = useState<string[]>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageSize: 10,
    pageNumber: 1,
  });

  useEffect(() => {
    const authUserString = localStorage.getItem("authPOSUser");
    if (authUserString) {
      const authUser = JSON.parse(authUserString);
      const permissionData = authUser?.permissions || [];
      setPermissionDetails(permissionData);
    }
  }, []);

  const HasPermission = useCallback((permission: string): boolean => {
    return permissionDetails.includes(permission);
  }, [permissionDetails]);

  const dataSource = useMemo(() => {
    return new CustomStore({
      key: 'id',
      load: async (loadOptions) => {
        try {
          const params = new URLSearchParams();
          params.set('skip', String(loadOptions.skip ?? 0));
          params.set('take', String(loadOptions.take ?? 10));
          params.set('sort', JSON.stringify(loadOptions.sort ?? []));
          params.set('filter', JSON.stringify(loadOptions.filter ?? null));
          params.set('searchExpr', JSON.stringify(loadOptions.searchExpr ?? null));
          params.set('searchOperation', loadOptions.searchOperation ?? '');
          params.set('searchValue', loadOptions.searchValue ?? '');
          
          const response = await getAllEnquiryData(params);
          
          if (!response?.data || response.data.length === 0) {
            return { data: [], totalCount: 0 };
          }
          
          return response;
        } catch (error) {
          console.error('Error loading enquiry data:', error);
          return { data: [], totalCount: 0 };
        }
      },
    });
  }, []);

  const onPagingChange = useCallback(({ pageSize, pageIndex }: { pageSize?: number; pageIndex?: number }) => {
    setPagination(prev => ({
      pageSize: pageSize ?? prev.pageSize,
      pageNumber: (pageIndex ?? prev.pageNumber - 1) + 1,
    }));
  }, []);

  const renderSerialNumber = useCallback((cellData: any) => {
    const rowIndex = cellData.rowIndex;
    const currentPage = pagination.pageNumber;
    const pageSize = pagination.pageSize;
    const serialNumber = (currentPage - 1) * pageSize + rowIndex + 1;
    return <span>{serialNumber}</span>;
  }, [pagination.pageNumber, pagination.pageSize]);

  return (
    <React.Fragment>
      {loading && <Loader />}
      <div className="page-content">
        <div className="container-fluid">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="m-0">Enquiry Master</h5>
            </Card.Header>
            <Card.Body className="pt-0">
              {HasPermission("SalesPerson.Write") ?
                <DataGrid
                  ref={dataGridRef}
                  dataSource={dataSource}
                  remoteOperations={{ paging: true }}
                  onOptionChanged={(e) => {
                    if (
                      e.fullName === "paging.pageIndex" ||
                      e.fullName === "paging.pageSize"
                    ) {
                      onPagingChange({
                        pageIndex:
                          e.fullName === "paging.pageIndex"
                            ? e.value
                            : pagination.pageNumber - 1,
                        pageSize:
                          e.fullName === "paging.pageSize"
                            ? e.value
                            : pagination.pageSize,
                      });
                    }
                  }}
                  showRowLines={true}
                  keyExpr="id"
                  showBorders={true}
                >
                  <Pager
                    visible={true}
                    showPageSizeSelector={true}
                  allowedPageSizes={[10, 25, 50, 100]}
                    showNavigationButtons={true}
                    displayMode="full"
                    showInfo={true}
                  />
                  <Paging pageSize={pagination?.pageSize} />
                  <Column
                    caption="Sr.No."
                    dataType="number"
                    alignment="center"
                    cellRender={renderSerialNumber}
                  />
                  <Column
                    dataField="customerName"
                    caption="Customer Name"
                    dataType="string"
                  />
                  <Column
                    dataField="emailId"
                    caption="Email Id"
                    dataType="string"
                  />
                  <Column
                    dataField="purpose"
                    caption="Purpose"
                    dataType="string"
                  />
                  <Column
                    dataField="mobileNo"
                    caption="Mobile Number"
                    dataType="string"
                  />
                  <Column
                    dataField="status"
                    caption="Status"
                    dataType="string"
                  />
                  <Column
                    dataField="type"
                    caption="Type"
                    dataType="string"
                  />
                </DataGrid>
                :
                <Result
                  status="403"
                  title="401"
                  subTitle="Sorry, you are not authorized to access this data."
                  className="custom-result"
                />
              }
            </Card.Body>
          </Card>
        </div>
      </div>
    </React.Fragment>)
}

export default EnquiryMasterMain;